import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageBackground from '@/components/PageBackground';
import MobileBackButton from '@/components/MobileBackButton';
import { searchAllStoreProducts } from '@/lib/storeProducts';
import ProductCard from '@/components/store/ProductCard';
import { useCart } from '@/contexts/CartContext';
import { ToastContext } from '@/components/ToastProvider';
import CooperativeStore from './CooperativeStore';
import PreOrderChat from '@/components/PreOrderChat.jsx';
import { createPortal } from 'react-dom';
import { MessageCircle, Zap } from 'lucide-react';
import MemberStoreProducts from '@/components/store/MemberStoreProducts';
import QuickViewModal from '@/components/store/QuickViewModal';
import PromoSplashScreen from '@/components/PromoSplashScreen';
import SignUpModal from '@/components/SignUpModal';
import { useAuth } from '@/AuthContext';
import { db } from '@/lib/firebase';
import { collection, query, where, limit, onSnapshot, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';


export default function Stores() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('cooperative');
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  const [globalSearchResults, setGlobalSearchResults] = useState([]);
  const [isGlobalSearching, setIsGlobalSearching] = useState(false);
  const { addItem, totalQty } = useCart();
  const { notify } = React.useContext(ToastContext);
  
  // Enhanced cart and quick view state
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [showQuickView, setShowQuickView] = useState(false);
  
  // Orders count (ongoing orders only, excluding completed)
  const [ordersCount, setOrdersCount] = useState(0);
  
  // Promotion splash screen state
  const [showPromoSplash, setShowPromoSplash] = useState(false);
  
  // SignUpModal state
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  
  // Category management state
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cooperativeStoreId, setCooperativeStoreId] = useState(null);
  const [cooperativeStoreName, setCooperativeStoreName] = useState('Cooperative Store');
  const [chatOpen, setChatOpen] = useState(false);
  const [hasUnreadChat, setHasUnreadChat] = useState(false);

     // Store Tabs Configuration
   const storeTabs = [
     {
       id: 'cooperative',
       name: 'Coop',
       description: 'Fresh produce, groceries, and local goods from our cooperative'
     },
     {
       id: 'member',
       name: 'Member',
       description: 'Products from our cooperative members'
     },
     {
       id: 'merchant',
       name: 'Merchant',
       description: 'Products from local merchants and businesses'
     }
   ];

     // Global search across all stores
   const handleGlobalSearch = async (searchTerm) => {
     if (!searchTerm.trim()) {
       setGlobalSearchResults([]);
       setIsGlobalSearching(false);
       return;
     }
 
    setIsGlobalSearching(true);
    try {
      // Search across all store-specific collections
      const searchResults = await searchAllStoreProducts(searchTerm, 100);
      
      // searchAllStoreProducts already does the filtering, so use results directly
      
      setGlobalSearchResults(searchResults);
     } catch (error) {
       console.error('Error searching products:', error);
       notify('Error searching products', 'error');
     } finally {
       setIsGlobalSearching(false);
     }
   };

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleGlobalSearch(globalSearchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [globalSearchTerm]);

  // Show promotion splash screen when store loads
  useEffect(() => {
    // Check if we should show promotions (not shown today)
    const today = new Date().toDateString();
    const lastShown = localStorage.getItem('promoLastShown');
    
    if (lastShown !== today) {
      // Show after a short delay to let the page load
      const timer = setTimeout(() => {
        setShowPromoSplash(true);
        localStorage.setItem('promoLastShown', today);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  // Orders count (ongoing orders only, excluding completed)
  useEffect(() => {
    if (!user?.uid) { 
      setOrdersCount(0); 
      return; 
    }
    
    const q = query(
      collection(db, 'orders'),
      where('userId', '==', user.uid),
      limit(100)
    );
    
    const unsub = onSnapshot(q, (snap) => {
      let count = 0;
      snap.forEach((doc) => {
        const data = doc.data();
        const status = String(data?.status || '');
        // Count only ongoing orders (exclude completed)
        if (status && status !== 'completed' && status !== 'cancelled') {
          count++;
        }
      });
      setOrdersCount(count);
    }, (error) => {
      setOrdersCount(0);
    });
    
    return () => unsub();
  }, [user?.uid]);

  // Load cooperative store ID and name
  useEffect(() => {
    const getCooperativeStoreId = async () => {
      try {
        const storesQuery = query(
          collection(db, 'stores'),
          where('type', '==', 'cooperative'),
          limit(1)
        );
        const snapshot = await getDocs(storesQuery);
        if (!snapshot.empty) {
          const storeDoc = snapshot.docs[0];
          setCooperativeStoreId(storeDoc.id);
          setCooperativeStoreName(storeDoc.data()?.name || 'Cooperative Store');
        }
      } catch (error) {
        console.error('Error fetching cooperative store:', error);
      }
    };
    
    getCooperativeStoreId();
  }, []);

  // Subscribe to user's pre-order thread for unread badge once we have storeId and user
  useEffect(() => {
    if (!user?.uid || !cooperativeStoreId) {
      setHasUnreadChat(false);
      return;
    }
    const tid = `${cooperativeStoreId}__${user.uid}`;
    const tRef = doc(db, 'preOrderThreads', tid);
    const unsub = onSnapshot(tRef, (snap) => {
      if (!snap.exists()) { setHasUnreadChat(false); return; }
      const data = snap.data() || {};
      const lma = data.lastMessageAt?.toMillis ? data.lastMessageAt.toMillis() : (data.lastMessageAt?._seconds ? data.lastMessageAt._seconds * 1000 : 0);
      const lr = data.lastRead?.[user.uid];
      const lrt = lr?.toMillis ? lr.toMillis() : (lr?._seconds ? lr._seconds * 1000 : 0);
      setHasUnreadChat(Boolean(lma && (!lrt || lma > lrt)));
    }, () => setHasUnreadChat(false));
    return () => unsub();
  }, [user?.uid, cooperativeStoreId]);

  // Handle URL parameters for affiliate links and product navigation
  useEffect(() => {
    const productId = searchParams.get('product');
    const affiliateCode = searchParams.get('affiliate');
    const ref = searchParams.get('ref');

    // If there's a product parameter, navigate to the product detail page
    if (productId) {
      // Store affiliate tracking info in localStorage for later use
      if (affiliateCode) {
        localStorage.setItem('affiliateCode', affiliateCode);
        localStorage.setItem('affiliateRef', ref || 'affiliate');
      }
      
      // Navigate to the product detail page
      navigate(`/store/${productId}`, { replace: true });
      return;
    }

    // If there's an affiliate code but no product, just store the affiliate info
    if (affiliateCode) {
      localStorage.setItem('affiliateCode', affiliateCode);
      localStorage.setItem('affiliateRef', ref || 'affiliate');
    }
  }, [searchParams, navigate]);

  // Load categories based on active tab and store
  useEffect(() => {
    if (activeTab === 'cooperative' && cooperativeStoreId) {
      const unsub = onSnapshot(
        query(
          collection(db, 'storeCategories'),
          where('storeId', '==', cooperativeStoreId)
        ),
        (snapshot) => {
          const cats = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          
          const dynamicCategories = [
            {
              id: 'all',
              name: 'All Products',
              icon: '🛍️',
              description: 'Browse all cooperative products'
            }
          ];

          cats.forEach(cat => {
            if (cat.isActive) {
              dynamicCategories.push({
                id: cat.id,
                name: cat.name,
                icon: cat.icon || '🏷️',
                originalName: cat.name,
                storeSection: cat.storeSection,
                sortOrder: cat.sortOrder || 999
              });
            }
          });

          // If no store-specific categories found, add default ones
          if (cats.length === 0) {
            const defaultCategories = [
              {
                id: 'gulay-lowland',
                name: 'Gulay LowLand',
                icon: '🥬',
                originalName: 'Gulay LowLand',
                storeSection: 'fresh',
                sortOrder: 1
              },
              {
                id: 'condiments-spices',
                name: 'Condiments/Spices',
                icon: '🌶️',
                originalName: 'Condiments/Spices',
                storeSection: 'fresh',
                sortOrder: 2
              },
              {
                id: 'rice-grains',
                name: 'Rice & Grains',
                icon: '🌾',
                originalName: 'Rice & Grains',
                storeSection: 'grocery',
                sortOrder: 3
              }
            ];
            defaultCategories.forEach(cat => dynamicCategories.push(cat));
          }

          // Sort categories by sortOrder
          dynamicCategories.sort((a, b) => {
            if (a.id === 'all') return -1;
            if (b.id === 'all') return 1;
            return (a.sortOrder || 999) - (b.sortOrder || 999);
          });

          setCategories(dynamicCategories);
          setCategoriesLoading(false);
        },
        (error) => {
          // If error, use default categories
          const defaultCategories = [
            {
              id: 'all',
              name: 'All Products',
              icon: '🛍️',
              description: 'Browse all cooperative products'
            },
            {
              id: 'gulay-lowland',
              name: 'Gulay LowLand',
              icon: '🥬',
              originalName: 'Gulay LowLand',
              storeSection: 'fresh',
              sortOrder: 1
            }
          ];
          setCategories(defaultCategories);
          setCategoriesLoading(false);
        }
      );
      return () => unsub();
    } else if (activeTab === 'member') {
      // For member stores, just show All Products for now
      setCategories([
        {
          id: 'all',
          name: 'All Products',
          icon: '🛍️',
          description: 'Browse all member store products'
        }
      ]);
      setCategoriesLoading(false);
    } else if (activeTab === 'merchant') {
      // For merchant stores, show coming soon
      setCategories([
        {
          id: 'coming-soon',
          name: 'Coming Soon',
          icon: '🏬',
          description: 'Merchant stores will be available soon'
        }
      ]);
      setCategoriesLoading(false);
    }
  }, [activeTab, cooperativeStoreId]);

  // Reset selected category when switching tabs
  useEffect(() => {
    setSelectedCategory('all');
  }, [activeTab]);

  const handleAddToCart = (product) => {
    // Check if user is logged in
    if (!user) {
      notify('Please sign in to add items to cart', 'info');
      return;
    }
    
    // Use the quantity passed from ProductCard, fallback to quantityStep or 1
    const qty = product.qty || product.quantityStep || 1;
    addItem({ 
      ...product, 
      qty: qty,
      storeType: product.storeType || 'cooperative',
      storeId: product.storeId || 'cooperative'
    });
    notify(`${product.name} added to cart!`, 'success');
  };

  const handleProductClick = (product) => {
    // Navigate to product detail page
    navigate(`/store/${product.id}`);
  };

  const handleQuickView = (product) => {
    setQuickViewProduct(product);
    setShowQuickView(true);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'cooperative':
        return <CooperativeStore userLoggedIn={!!user} selectedCategory={selectedCategory} />;
      case 'member':
        return <MemberStoreProducts userLoggedIn={!!user} selectedCategory={selectedCategory} />;
      case 'merchant':
        return (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🏬</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Merchant Stores</h3>
            <p className="text-gray-600 mb-4">
              Coming soon! Merchant stores will be available here.
            </p>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 max-w-md mx-auto">
              <p className="text-purple-800 text-sm">
                Local merchants and businesses will be able to showcase their products here.
              </p>
            </div>
          </div>
        );
      default:
        return <CooperativeStore userLoggedIn={!!user} selectedCategory={selectedCategory} />;
    }
  };

  const renderGlobalSearchResults = () => {
    if (!globalSearchTerm.trim()) return null;

    return (
      <div className="mb-8">
        <div className="bg-white rounded-lg border shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            🔍 Global Search Results for "{globalSearchTerm}"
          </h3>
          
          {isGlobalSearching ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <span className="ml-2 text-gray-600">Searching all stores...</span>
            </div>
          ) : globalSearchResults.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-2">🔍</div>
              <p className="text-gray-600">No products found matching "{globalSearchTerm}" across all stores.</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-4">
                Found {globalSearchResults.length} product{globalSearchResults.length !== 1 ? 's' : ''} across all stores
              </p>
                             <div className="grid grid-cols-2 gap-4" style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem'}}>
                 {globalSearchResults.map((product) => (
                   <ProductCard
                     key={product.id}
                     p={product}
                     onClick={() => handleProductClick(product)}
                     onAdd={(itemWithQty) => handleAddToCart(itemWithQty)}
                     showAdd={user ? true : false}
                     showQuick={true}
                     onQuick={() => handleQuickView(product)}
                     userLoggedIn={!!user}
                   />
                 ))}
               </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const contentTopSpacer = activeTab === 'member' ? '2rem' : '3rem';
  const contentPaddingTop = user
    ? `calc(var(--status-bar-height, 0px) + 4rem + 2.5rem + 2.25rem + ${contentTopSpacer} + 0.5rem)`
    : `calc(var(--status-bar-height, 0px) + 4rem + 2.5rem + 2.25rem + ${contentTopSpacer} + 1.5rem + 0.5rem)`;

  return (
    <>
      {/* Store Header - Fixed seamlessly below main header */}
      <div className="fixed left-0 right-0 z-[45] bg-green-600 shadow-sm border-0" style={{top: 'calc(var(--status-bar-height, 0px) + 4rem)'}}>
        <div className="w-full">
          <div className="flex items-center justify-between py-1 px-4">
            <h1 className="text-sm font-semibold text-white">
              🛍️ Stores
            </h1>
            
            {/* Search Bar and Icons in one row */}
            <div className="flex items-center space-x-1.5">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search stores..."
                  value={globalSearchTerm}
                  onChange={(e) => setGlobalSearchTerm(e.target.value)}
                  className="w-32 sm:w-40 md:w-56 lg:w-64 pl-3 pr-8 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                {globalSearchTerm && (
                  <button
                    onClick={() => setGlobalSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>
              
              {/* My Orders Icon - Only show for logged-in users */}
              {user && (
                <div className="relative">
                    <button 
                      onClick={() => navigate('/orders')}
                      className="p-1 text-white hover:text-green-100 relative hover:bg-green-700 rounded-lg transition-colors border border-white/50 hover:border-white shadow-md"
                      title="My Orders"
                    >
                    📋
                    {ordersCount > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-xs font-bold leading-[16px] text-center shadow-lg border-2 border-white">
                        {ordersCount > 99 ? '99+' : ordersCount}
                      </span>
                    )}
                  </button>
                </div>
              )}
              
              {/* Promotions Icon */}
              <div className="relative">
                  <button 
                    onClick={() => setShowPromoSplash(true)}
                    className="p-1 text-white hover:text-green-100 relative hover:bg-green-700 rounded-lg transition-colors"
                    title="View Promotions"
                  >
                  🎉
                </button>
              </div>
              
              {/* Cart Icon - Show for everyone but with different behavior */}
              <div className="relative">
                  <button 
                    onClick={() => user ? navigate('/cart') : setShowQuickView(false)}
                    className="p-1 text-white hover:text-green-100 relative hover:bg-green-700 rounded-lg transition-colors border border-white/50 hover:border-white shadow-md"
                    title={user ? "Shopping Cart" : "Sign in to access cart"}
                  >
                  🛒
                  {totalQty > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                      {totalQty}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sign in prompt for non-logged-in users */}
      {!user && (
        <div className="fixed left-0 right-0 z-[40] bg-blue-50 border-b border-blue-200 px-4 py-1" style={{top: 'calc(var(--status-bar-height, 0px) + 4rem + 2.5rem)'}}>
          <div className="flex items-center justify-center space-x-2">
            <p className="text-xs text-blue-800">Browse our products!</p>
            <button
              onClick={() => setShowSignUpModal(true)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium underline"
            >
              Sign in for cart & orders
            </button>
          </div>
        </div>
      )}

      {/* Store Tabs - Fixed below search bar */}
      <div className="fixed left-0 right-0 z-[35] bg-white border-0" style={{top: user ? 'calc(var(--status-bar-height, 0px) + 4rem + 2.5rem)' : 'calc(var(--status-bar-height, 0px) + 4rem + 2.5rem + 1.5rem)'}}>
        <div className="w-full">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-4 px-4 py-1">
              {storeTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-1 px-2 border-b-2 font-medium text-sm transition-colors duration-200 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Category Navigation - Fixed below store tabs */}
      <div className="fixed left-0 right-0 z-[25] bg-white border-b border-gray-200 shadow-sm" style={{top: user ? 'calc(var(--status-bar-height, 0px) + 4rem + 2.5rem + 2.25rem)' : 'calc(var(--status-bar-height, 0px) + 4rem + 2.5rem + 2.25rem + 1.5rem)'}}>
        <div className="w-full px-4 py-1.5">
          {categoriesLoading ? (
            <div className="flex justify-center items-center py-1">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-600"></div>
              <span className="ml-2 text-xs text-gray-600">Loading...</span>
            </div>
          ) : (
            <div className="relative">
              <div className="overflow-x-auto scrollbar-hide auto-scroll-container">
                <div className={`flex gap-1.5 min-w-max ${categories.length > 3 ? 'auto-scroll-content' : ''}`}>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`px-2.5 py-1 rounded-lg border transition-all duration-200 text-xs whitespace-nowrap flex-shrink-0 ${
                        selectedCategory === category.id
                          ? 'bg-green-600 text-white border-green-600 shadow-lg'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-green-300'
                      }`}
                    >
                      <span className="mr-1">{category.icon}</span>
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <PageBackground 
        image="https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=1500&q=80"
        overlayClass="bg-white/90 backdrop-blur"
        boxed={false}
        pad="p-0"
        margin="my-0"
        className=""
        edgeToEdge={true}
      >
        <div className="min-h-screen">

        <div className="w-full py-2" style={{ paddingTop: contentPaddingTop }}>

          {/* Global Search Results */}
          {renderGlobalSearchResults()}

          {/* Tab Content */}
          <div className="min-h-96">
            {renderTabContent()}
          </div>
        </div>
        </div>
      </PageBackground>

      {/* Floating chat via portal to avoid container clipping; visible for all (prompts sign-in if needed) */}
      {typeof document !== 'undefined' && createPortal(
        <>
          <button
            type="button"
            aria-label="Chat with store"
            className="fixed w-14 h-14 bottom-6 right-6 rounded-full shadow-xl bg-green-600 hover:bg-green-700 text-white flex items-center justify-center z-[10000]"
            style={{ bottom: 'max(1rem, env(safe-area-inset-bottom))', right: 'max(1rem, env(safe-area-inset-right))' }}
            onClick={async () => {
              if (user) {
                setChatOpen(true);
                try {
                  if (cooperativeStoreId) {
                    const tid = `${cooperativeStoreId}__${user.uid}`;
                    await updateDoc(doc(db, 'preOrderThreads', tid), { [`lastRead.${user.uid}`]: serverTimestamp() }).catch(()=>{});
                  }
                } catch {}
              } else { setShowSignUpModal(true); }
            }}
          >
            <span className="sr-only">Open chat</span>
            <span className="relative inline-flex items-center justify-center">
              <MessageCircle size={28} strokeWidth={2.5} className="text-white" />
              <Zap size={14} strokeWidth={2.5} className="text-white absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
              {user && hasUnreadChat && (
                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-red-500 border-2 border-white" aria-label="Unread messages" />
              )}
            </span>
          </button>

          {chatOpen && user && (
            <div className="fixed inset-0 z-[10010] bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4">
              <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg">
                <div className="flex items-center justify-between p-3 border-b">
                  <div className="font-semibold">Chat with {cooperativeStoreName}</div>
                  <button className="text-sm text-gray-500 hover:text-gray-800" onClick={() => setChatOpen(false)}>✕</button>
                </div>
                <div className="p-3">
                  {cooperativeStoreId ? (
                    <PreOrderChat storeId={cooperativeStoreId} storeType="cooperative" senderRole="customer" />
                  ) : (
                    <div className="text-sm text-gray-600 py-8 text-center">Preparing chat…</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>,
        document.body
      )}
      
      {/* Promotion Splash Screen */}
      <PromoSplashScreen
        isOpen={showPromoSplash}
        onClose={() => setShowPromoSplash(false)}
        userId={user?.uid}
      />
      
      {/* Quick View Modal */}
      <QuickViewModal
        product={quickViewProduct}
        isOpen={showQuickView}
        onClose={() => {
          setShowQuickView(false);
          setQuickViewProduct(null);
        }}
      />
      
      {/* SignUp Modal */}
      <SignUpModal
        isOpen={showSignUpModal}
        onClose={() => setShowSignUpModal(false)}
        onSignUpSuccess={(user) => {
          setShowSignUpModal(false);
          notify('Account created successfully! You can now use the cart and place orders.', 'success');
        }}
      />
    </>
   );
 }
